import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import jsPDF from "jspdf";
import {
  Mail,
  Printer,
  Send,
  X,
  Download,
  Copy,
  Building2,
  User,
  Plus,
  ArrowLeft,
  Loader2,
} from "lucide-react";
import { ActionType } from "@/enum/ActionType";
import PatientConsultationResponse from "@/utils/PatientConsultationResponse";
import { updateConsultationActionsV2Url } from "@/apiUrls/apiUrls";
import {
  setIncrementReferralCompleted,
  setAddPatientAIChatMessage,
  SaveAIChatMessage,
  setLetterDetails,
  setChatBotReferralLetter,
  setReferralLetter,
  trackServiceBilling,
} from "@/dispatchers/patientDispatchers";
import removeActionFromLocal from "@/utils/removeFromLocalStorage";
import { useReferralLetterData } from "@/lib/settings/ReferralLetterData";
import useTitanApp from "@/hooks/useTitanApp";
import AiStreamResponse from "@/utils/AiStreamResponse";
import {
  generateReferalLetterUrl,
  generateChatBotReferalLetterUrl,
} from "@/apiUrls/apiUrls";
import { v4 as uuidv4 } from "uuid";

interface RecipientInfo {
  email: string;
  label: string;
  type: "surgery" | "patient" | "pharmacy" | "custom";
}

interface ReferralModalProps {
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSave?: (referral: any) => void;
  initialOpen?: boolean;
  initialTitle?: string;
  initialSubject?: string;
  initialContent?: string;
  onClose?: () => void;
  onBack?: () => void;
  documentType?: "email" | "sms" | "letter";
  patientName?: string;
  patientEmail?: string;
  patientSurgery?: string;
}

export function ReferralModal({
  trigger,
  open,
  onOpenChange,
  onSave,
  initialOpen,
  initialTitle,
  initialSubject,
  initialContent,
  onClose,
  onBack,
  documentType,
  patientName,
  patientEmail,
  patientSurgery,
}: ReferralModalProps) {
  const { consultationId, referralCompleted, currentPharmacy } =
    useReferralLetterData();
  const { patientState, appState } = useTitanApp();
  const { darkMode, currentUser, currentPharmacyId, companyId } =
    appState || {};
  const {
    patientSummary,
    selectedPatientDetails,
    chatQuestions,
    referralLetter: existingReferralLetter,
    chatBotReferralLetter: existingChatBotReferralLetter,
    isChatReferralLetter: existingIsChatReferralLetter,
  } = patientState || {};
  const { referralLetter, chatBotReferralLetter, isChatReferralLetter } =
    patientState || {};
  const [internalOpen, setInternalOpen] = useState(initialOpen ?? false);
  const dialogOpen = open !== undefined ? open : internalOpen;
  const [documentTitle, setDocumentTitle] = useState(
    initialTitle || "Medical Letter",
  );
  const [emailSubject, setEmailSubject] = useState(initialSubject || "");
  const [generatedContent, setGeneratedContent] = useState(
    initialContent || "",
  );
  const [recipients, setRecipients] = useState<RecipientInfo[]>([]);
  const [isQueueingEmail, setIsQueueingEmail] = useState(false);
  const [togglePlaceholderModal, settogglePlaceholderModal] = useState(false);
  const [placeHolderForModal, setPlaceHolderForModal] = useState<string[]>([]);

  const accumulatedText = useRef("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamedText, setStreamedText] = useState("");

  const placeholders = [
  // "patient.title",
  // "patient.firstName",
  // "patient.lastName",
  // "patient.dob",
  // "patient.address",
  // "patient.email",
  // "patient.phone",
  // "appointment.date",
  // "appointment.time",
  // "appointment.duration",
  // "appointment.service",
  // "practice.name",
  // "practice.email",
  // "practice.phone",
  // "practice.address",
  // "pharmacy.name",
  // "pharmacy.phone",
  // "pharmacy.address",
  // "location.name",
  // "location.address",
  // "prescriber.name",
  // "prescriber.title",
  // "bookingLink",
  // "link.reschedule",
  // "link.cancel",
  // "PATIENT_NAME",
  // "DATE",
  // "patient.first",
  // "patient.last",
  // "clinician.email",
  // "appointment.type",
  // "appointment.reference",
  // "appointment.price",
  // "booking.start.time",
  // "booking.start.date",
  "Patient Name",
  "Title",
  "First Name",
  "Last Name",
  "patient@email.com",
  "Phone",
  "Time",
  "Duration",
  "Service",
  "Booking Link",
  "Reschedule Link",
  "Cancel Link",
  "Date",
  "Dob",
  "Address",
  "Practice Name",
  "Practice address",
  "Practice Email",
  "Practice Phone",
  "Prescriber Name",
  "Prescriber title",
];


  const flattenSummaryToString = (summaryObj: any) => {
    if (!summaryObj) return "";

    if (typeof summaryObj === "string") {
      return summaryObj.trim();
    }

    if (typeof summaryObj !== "object") {
      return "";
    }

    return Object.entries(summaryObj)
      .filter(
        ([key, value]) =>
          key !== "Actions" &&
          key !== "SummaryText" &&
          value !== null &&
          value !== undefined &&
          value !== "",
      )
      .map(([key, value]) => `${key}: ${value}`)
      .join(". ");
  };

  const summaryText =
    patientSummary?.SummaryText || flattenSummaryToString(patientSummary);
  const finalString = {
    summary: summaryText,
  };
  const patientname =
    `${selectedPatientDetails?.foreName} ${selectedPatientDetails?.surName}`.trim();
  const dobRaw = selectedPatientDetails?.dob;
  const dob = dobRaw
    ? new Intl.DateTimeFormat("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(new Date(dobRaw))
    : "";

  const chatPrompt = chatQuestions?.trim?.() || "";
  const shouldUseChatReferral = existingIsChatReferralLetter && !!chatPrompt;
  const params = shouldUseChatReferral ? { prompt: chatPrompt } : finalString;

  const getPatientLetter = async () => {
    try {
      const apiUrl = shouldUseChatReferral
        ? generateChatBotReferalLetterUrl()
        : summaryText
          ? generateReferalLetterUrl()
          : null;

      if (!apiUrl) {
        console.warn(
          "Referral letter generation skipped: no chat prompt or summary text available.",
        );
        return;
      }

      setIsStreaming(true);
      setStreamedText("");
      accumulatedText.current = "";

      let finalTitle = "";
      let finalContent = "";

      await AiStreamResponse(
        params,
        (chunk) => {
          accumulatedText.current += chunk;
          const cleaned = accumulatedText.current.replace(/[{}]/g, "").trim();

          const titleMatch = cleaned.match(/"Title"\s*:\s*"([^"]*)"/);
          const contentMatch = cleaned.match(/"Content"\s*:\s*"([\s\S]*)$/);

          if (titleMatch) {
            finalTitle = titleMatch[1];
            setIsStreaming(false);
            setDocumentTitle(finalTitle);
            setEmailSubject(finalTitle);
          }

          if (contentMatch) {
            finalContent = contentMatch[1]
              .replace(/\\"/g, '"')
              .replace(/\\n/g, "\n")
              .replace(/\[Patient Name\]/gi, patientname)
              .replace(/\[DOB\]/gi, dob)
              .replace(/\[YOUR_NAME_PLACEHOLDER\]/gi, currentUser?.fullName)
              .replace(/```/g, "")
              .replace(/"\s*$/, "");

            setGeneratedContent(finalContent);
          }
        },
        async () => {
          const completedLetter = {
            title: finalTitle || "Referral Letter",
            subject: finalTitle || "Referral Letter",
            content: finalContent,
            recordedAt: null,
            recipients: [],
          };

          setLetterDetails(completedLetter);
          if (existingIsChatReferralLetter) {
            setChatBotReferralLetter(completedLetter);
          } else {
            setReferralLetter(completedLetter);
          }
          setIsStreaming(false);
          try {
            if (consultationId) {
              await trackServiceBilling({
                companyId,
                consultationId,
                pharmacyId: currentPharmacyId,
                actiontype: "aicall",
              });

              await trackServiceBilling({
                companyId,
                consultationId,
                pharmacyId: currentPharmacyId,
                actiontype: "referralletter",
              });
            }
          } catch (err) {
            console.error(
              "Error calling billing tracker after AI generation:",
              err,
            );
          }
        },
        apiUrl,
      );
    } catch (error) {
      console.error("Streaming failed", error);
      setIsStreaming(false);
    }
  };

  useEffect(() => {
    const existingLetter = existingIsChatReferralLetter
      ? existingChatBotReferralLetter
      : existingReferralLetter;
    const hasExistingLetter =
      existingLetter?.title?.trim() || existingLetter?.content?.trim();

    if (hasExistingLetter) {
      const letterWithRecordedAt = {
        title: existingLetter.title || "",
        subject: existingLetter.subject || existingLetter.title || "",
        content: existingLetter.content || "",
        recordedAt: existingLetter.recordedAt || null,
        recipients: existingLetter.recipients || [],
      };

      setDocumentTitle(letterWithRecordedAt.title || "Medical Letter");
      setEmailSubject(letterWithRecordedAt.subject || "");
      setGeneratedContent(letterWithRecordedAt.content || "");
      setRecipients(letterWithRecordedAt.recipients || []);
    } else {
      // getPatientLetter();
    }
  }, [
    existingReferralLetter,
    existingChatBotReferralLetter,
    existingIsChatReferralLetter,
  ]);

  useEffect(() => {
    setDocumentTitle(initialTitle || "Medical Letter");
    setEmailSubject(initialSubject || "");
    setGeneratedContent(initialContent || "");
  }, [initialTitle, initialSubject, initialContent]);
  const [newEmail, setNewEmail] = useState("");
  const [showRecipientPicker, setShowRecipientPicker] = useState(false);
  const [includeEmailSignature, setIncludeEmailSignature] = useState(false);
  const [emailCopyToPatient, setEmailCopyToPatient] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Look up pharmacy email from currentPharmacy
  const pharmacyEmail = currentPharmacy?.email;
  const surgeryTeamEmail = selectedPatientDetails?.surgery?.email;
  const surgeryTeamName = selectedPatientDetails?.surgery?.name;

  const suggestedRecipients: RecipientInfo[] = [
    ...(pharmacyEmail
      ? [
          {
            email: pharmacyEmail,
            label: currentPharmacy?.teamPharmacyName || "Pharmacy",
            type: "pharmacy" as const,
          },
        ]
      : []),
    ...(patientEmail
      ? [
          {
            email: patientEmail,
            label: patientName || "Patient",
            type: "patient" as const,
          },
        ]
      : []),
      ...(surgeryTeamEmail
      ? [
          {
            email: surgeryTeamEmail,
            label: surgeryTeamName || "Surgery Team",
            type: "surgery" as const,
          },
        ]
      : []),
  ];


 const validateEmail = (email : string) => {
     const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }

// useEffect(() => {
//   console.log(recipentEmailIsValid);
//   if(validateEmail(newEmail.trim())){
//     setrecipentEmailIsValid(true);
//   }
//   else{
//     setrecipentEmailIsValid(false);
//   }

// }, [newEmail]);

  const isRecipientAdded = (email: string) =>
    recipients.some((r) => r.email === email);

  // Initialize with sample generated content only when no initial content provided
  const addRecipient = (recipient: RecipientInfo) => {
    if (!isRecipientAdded(recipient.email)) {
      setRecipients((prev) => [...prev, recipient]);
    }
  };

  const addCustomEmail = () => {
    if (newEmail && !isRecipientAdded(newEmail) && validateEmail(newEmail)) {
      setRecipients((prev) => [
        ...prev,
        { email: newEmail, label: newEmail, type: "custom" },
      ]);
      setNewEmail("");
    }
  };

  const removeRecipient = (email: string) => {
    setRecipients((prev) => prev.filter((r) => r.email !== email));
  };

  const confirmIgnoreEmptyPlaceholders = () => {
    settogglePlaceholderModal(false);
    handleQueueEmail(true);
  };

   const validateEmptyPlaceholders = () => {
      const combinedContent = `${emailSubject} ${generatedContent}`;

     const placeholdersInContent = new Set(
    (combinedContent.match(/\[(.*?)\]/g) || [])
    .map(x => x.slice(1, -1))
    );

    const targetPlaceholders = placeholders.filter(p => placeholdersInContent.has(p));

    if (targetPlaceholders.length > 0) {
      setPlaceHolderForModal(targetPlaceholders);
      settogglePlaceholderModal(true);
      return false;
    }

    return true;
  };



  const convertTextToBase64Image = (title: string, content: string) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const width = 595;
    const height = 842;
    canvas.width = width;
    canvas.height = height;

    if (!ctx) return "";

    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, width, height);

    ctx.fillStyle = "#282828";
    ctx.font = "bold 24px Times New Roman";
    ctx.textBaseline = "top";

    const marginLeft = 40;
    let y = 40;

    ctx.fillText(title, marginLeft, y);
    y += 40;

    ctx.font = "normal 18px Times New Roman";
    const lineHeight = 26;
    const maxWidth = width - marginLeft * 2;

    const lines: string[] = [];
    const words = content.split(" ");
    let line = "";

    words.forEach((word) => {
      const testLine = line + word + " ";
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && line) {
        lines.push(line.trim());
        line = word + " ";
      } else {
        line = testLine;
      }
    });

    if (line) lines.push(line.trim());

    lines.forEach((textLine) => {
      if (y > height - 40) {
        return;
      }
      ctx.fillText(textLine, marginLeft, y);
      y += lineHeight;
    });

    return canvas.toDataURL("image/png");
  };

  const createPdfBlob = async (title: string, content: string) => {
    const doc = new jsPDF("p", "mm", "a4");

    const marginLeft = 20;
    const lineHeight = 8;
    const pageWidth = doc.internal.pageSize.getWidth() - marginLeft * 2;

    doc.setFont("Times", "Normal");
    doc.setFontSize(16);
    doc.setTextColor(40, 40, 40);

    doc.setFontSize(18);
    doc.setFont("Times", "Bold");
    doc.text(title, marginLeft, 30);

    doc.setFontSize(14);
    doc.setFont("Times", "Normal");

    const lines = doc.splitTextToSize(content, pageWidth);
    let y = 40;

    for (let i = 0; i < lines.length; i++) {
      if (y > 280) {
        doc.addPage();
        y = 20;
      }
      doc.text(lines[i], marginLeft, y);
      y += lineHeight;
    }

    return doc.output("blob");
  };

  const handleQueueEmail = async (ignorePlaceholderWarning = false) => {
    if (isQueueingEmail) {
      return;
    }

    if (recipients.length === 0) {
      alert("Please add at least one email recipient");
      return;
    }

    if (!ignorePlaceholderWarning && !validateEmptyPlaceholders()) {
        return;
    }
    
    setIsQueueingEmail(true);
    let pdfFile;
    let base64Image = "";
    let referralReference = uuidv4();
    const letterToSave = {
      title: documentTitle,
      subject: emailSubject,
      content: generatedContent,
      recipients: recipients.map((r) => ({
        email: r.email,
        label: r.label,
        type: r.type,
      })),
      recordedAt: new Date().toISOString(),
      status: "Queued",
      documentType: documentType,
      referralReference: referralReference,
    };
    pdfFile = await createPdfBlob(letterToSave.title, letterToSave.content);
    base64Image = convertTextToBase64Image(
      letterToSave.title,
      letterToSave.content,
    );

    const letterPayload = {
      actionName: ActionType[3],
      actionNumber: "3",
      payload: JSON.stringify({
        emailRecipients: letterToSave.recipients,
        includeEmailSignature,
        emailCopyToPatient,
        letterContent: letterToSave,
        recordedAt: letterToSave.recordedAt,
        recipients: letterToSave.recipients,
        status: "Queued",
        documentType: documentType,
        referralReference: referralReference,
      }),
      isPerformed: true,
      image: base64Image,
      documentType: documentType,
    };

    try {
      let result = await PatientConsultationResponse(
        { pdfFile, payload: letterPayload },
        () => {},
        updateConsultationActionsV2Url(consultationId),
      );
      if (result?.response === true) {
        setIncrementReferralCompleted(true);
        const referralMessage = [
          {
            sender: "success",
            message: "Referral letter completed successfully.",
            actionId: 3,
            details: letterToSave,
          },
        ];
        await SaveAIChatMessage({
          ConsultationChat: JSON.stringify(referralMessage),
          ConsultationId: consultationId,
        });
        setAddPatientAIChatMessage({
          sender: "success",
          message: "Referral letter completed successfully.",
          actionId: 3,
          details: letterToSave,
        });
      }

      if (consultationId) {
        removeActionFromLocal(consultationId, "3");
      }

      if (onSave) {
        onSave({
          ...letterToSave,
          status: "queued",
          timestamp: new Date(),
          pdfFile,
          image: base64Image,
          payload: letterPayload,
        });
      }

      handleOpenChange(false);
    } catch (error) {
      console.error("Error queueing email:", error);
      alert("Failed to queue email. Please try again.");
    } finally {
      setIsQueueingEmail(false);
    }
  };

  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${documentTitle}</title>
            <style>
              body { 
                font-family: 'Times New Roman', serif; 
                font-size: 12pt; 
                line-height: 1.6; 
                margin: 2cm; 
                white-space: pre-wrap;
              }
              @media print {
                body { margin: 1cm; }
              }
            </style>
          </head>
          <body>${generatedContent.replace(/\n/g, "<br>")}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(generatedContent).then(() => {
      setShowSuccessModal(true);
    });
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      putOnlyUsedFonts: true,
      floatPrecision: 16,
    });

    // Set font to ensure proper encoding
    doc.setFont("helvetica", "normal");

    // Clean the content more thoroughly
    const cleanContent = generatedContent
      .replace(/&[a-zA-Z0-9#]+;/g, "") // Remove HTML entities
      .replace(/[^\x20-\x7E\n\r\t]/g, "") // Remove non-ASCII characters
      .replace(/\r\n/g, "\n") // Normalize line endings
      .replace(/\r/g, "\n")
      .replace(/\n{3,}/g, "\n\n") // Limit consecutive newlines
      .trim();

    const lines = cleanContent.split("\n");
    let yPosition = 25;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 25;
    const maxWidth = doc.internal.pageSize.width - 2 * margin;
    const lineHeight = 7;

    // Add title
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    const cleanTitle = documentTitle.replace(/[^\x20-\x7E]/g, "").trim();
    const titleLines = doc.splitTextToSize(cleanTitle, maxWidth);
    doc.text(titleLines, margin, yPosition);
    yPosition += titleLines.length * lineHeight + 10;

    // Add content
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");

    lines.forEach((line) => {
      const cleanLine = line.replace(/[^\x20-\x7E]/g, "").trim();

      if (cleanLine === "") {
        yPosition += lineHeight;
        return;
      }

      // Split long lines
      const lineParts = doc.splitTextToSize(cleanLine, maxWidth);

      lineParts.forEach((part: string) => {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          yPosition = 25;
        }
        doc.text(part, margin, yPosition);
        yPosition += lineHeight;
      });
    });

    // Save the PDF
    const fileName = `${
      cleanTitle
        .replace(/[^a-z0-9\s]/gi, "_")
        .replace(/\s+/g, "_")
        .toLowerCase() || "referral_letter"
    }.pdf`;
    doc.save(fileName);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }

    if (!newOpen) {
      onClose?.();
    }
  };

  const recipentEmailIsValid = validateEmail(newEmail.trim());
 
  return (
    <>
      <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
        {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
        <DialogContent className="max-w-6xl h-[90vh] overflow-hidden p-0 flex flex-col">
          <DialogHeader className="p-6 pb-3 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              {onBack && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    handleOpenChange(false);
                    onBack?.();
                  }}
                  className="p-1 h-auto text-gray-500 hover:text-gray-900"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              )}
              <div className="flex-1 min-w-0 space-y-1">
                <DialogTitle asChild>
                  <Input
                    value={documentTitle}
                    onChange={(e) => setDocumentTitle(e.target.value)}
                    className="text-xl font-semibold border-0 focus-visible:ring-0 focus-visible:ring-offset-0 p-0 h-auto bg-transparent"
                  />
                </DialogTitle>
              </div>
            </div>
          </DialogHeader>

          <div className="flex flex-1 overflow-hidden">
            {/* Main Content Panel */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 p-6 overflow-hidden flex flex-col gap-3">
                {(documentType === "email" ||
                  documentType === "letter" ||
                  !documentType) && (
                  <div className="flex-shrink-0">
                    <Label className="text-xs text-gray-500 mb-1 block">
                      Subject
                    </Label>
                    <Input
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Enter subject line"
                      className="w-full"
                    />
                  </div>
                )}
                <div className="flex-1 bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col">
                  <div className="h-full overflow-y-auto">
                    <Textarea
                      value={generatedContent}
                      onChange={(e) => setGeneratedContent(e.target.value)}
                      className="w-full h-full min-h-full resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 font-serif text-sm leading-relaxed p-6"
                      placeholder="Edit your document content here..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Actions Panel */}
            <div className="w-80 border-l border-gray-200 bg-gray-50 p-6 flex flex-col overflow-y-auto">
              <div className="space-y-6 flex-1">
                <h3 className="font-semibold text-gray-900">Actions</h3>

                {/* Quick Actions */}
                <div className="space-y-3">
                  <Button
                    onClick={handlePrint}
                    className="w-full justify-start"
                    variant="outline"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Print Document
                  </Button>

                  <Button
                    onClick={handleCopyToClipboard}
                    className="w-full justify-start"
                    variant="outline"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy to Clipboard
                  </Button>

                  <Button
                    onClick={handleDownloadPDF}
                    className="w-full justify-start"
                    variant="outline"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                </div>

                {/* Email Recipients */}
                <div className="space-y-3">
                  <Label>Send to</Label>

                  {/* Suggested recipients as selectable items */}
                  {suggestedRecipients.length > 0 && (
                    <div className="space-y-1.5">
                      {suggestedRecipients.map((suggestion) => {
                        const isSelected = suggestion.email
                          ? isRecipientAdded(suggestion.email)
                          : false;
                        const hasEmail = !!suggestion.email;
                        return (
                          <button
                            key={suggestion.type + suggestion.label}
                            disabled={!hasEmail || !validateEmail(suggestion.email)}
                            onClick={() => {
                              if (!hasEmail || !validateEmail(suggestion.email)) return;
                              if (isSelected) {
                                removeRecipient(suggestion.email);
                              } else {
                                addRecipient(suggestion);
                              }
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors ${
                              !hasEmail
                                ? "bg-gray-50 border-gray-200 opacity-50 cursor-not-allowed"
                                : isSelected
                                  ? "bg-gray-100 border-gray-400"
                                  : "bg-white border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <div
                              className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                isSelected
                                  ? "bg-gray-900 border-gray-900"
                                  : hasEmail
                                    ? "border-gray-300"
                                    : "border-gray-200"
                              }`}
                            >
                              {isSelected && (
                                <svg
                                  className="w-2.5 h-2.5 text-white"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={3}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {suggestion.label}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {suggestion.email || "No email on file"}
                              </p>
                            </div>
                            <span
                              className={`flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500`}
                            >
                              {suggestion.type === "surgery"
                                ? "Surgery"
                                : suggestion.type === "pharmacy"
                                  ? "Pharmacy"
                                  : "Patient"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Custom added emails */}
                  {recipients.filter((r) => r.type === "custom").length > 0 && (
                    <div className="space-y-1.5">
                      {recipients
                        .filter((r) => r.type === "custom")
                        .map((recipient) => (
                          <div
                            key={recipient.email}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-400 bg-gray-100"
                          >
                            <div className="w-4 h-4 rounded border-2 bg-gray-900 border-gray-900 flex items-center justify-center flex-shrink-0">
                              <svg
                                className="w-2.5 h-2.5 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={3}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {recipient.email}
                              </p>
                            </div>
                            <button
                              onClick={() => removeRecipient(recipient.email)}
                              className="p-0.5 text-gray-400 hover:text-red-500 flex-shrink-0 transition-colors"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Add another recipient */}
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Add another email..."
                      className="flex-1 text-sm"
                      onKeyDown={(e) => e.key === "Enter" && addCustomEmail()}
                    />
                    <Button
                      onClick={addCustomEmail}
                      size="sm"
                      variant="outline"
                      disabled={!recipentEmailIsValid}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Send Email Button */}
                <div className="pt-4 border-t">
                  <Button
                  onClick={() => handleQueueEmail()}
                    className="w-full bg-black hover:bg-gray-800 text-white"
                    disabled={recipients.length === 0 || isQueueingEmail}
                  >
                    {isQueueingEmail ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    {isQueueingEmail
                      ? "Queueing Email..."
                      : `Queue Email (${recipients.length})`}
                  </Button>
                  <p className="text-xs text-gray-500 text-center mt-2">
                    Email will be sent when the consultation is completed
                  </p>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Modal for Copy to Clipboard */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-[400px] bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <svg
                className="w-5 h-5 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Success
            </DialogTitle>
          </DialogHeader>

          <p className="mt-2 text-sm text-gray-600">
            Document copied to clipboard successfully!
          </p>

          <div className="flex justify-end pt-6">
            <Button
              onClick={() => setShowSuccessModal(false)}
              className="px-6 text-white bg-green-600 hover:bg-green-700"
            >
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* placeholders modal */}
<Dialog open={togglePlaceholderModal} onOpenChange={settogglePlaceholderModal}>
  <DialogContent className="max-w-md">

    <DialogHeader>
      <DialogTitle>Empty Placeholders</DialogTitle>
    </DialogHeader>

    <div className="py-4">
        You have following Placeholders with no data.
        {placeHolderForModal.map((p, i) => (
          <div key={i}>
            {p}
          </div>
        ))}
    </div>

    {/* FOOTER */}
    <div className="flex justify-end gap-2 pt-4 border-t">

                  <Button
                  onClick={() => settogglePlaceholderModal(false)}
                    className="w-full bg-black hover:bg-gray-800 text-white"
                  >
                    Cancel Queue
                  </Button>

                    <Button
                    onClick={confirmIgnoreEmptyPlaceholders}
                    className="w-full bg-black hover:bg-gray-800 text-white"
                  >
                   Queue Anyway
                  </Button>

    </div>

  </DialogContent>
</Dialog>
    </>
  );
}
